import React, { useEffect, useRef, useState, useMemo } from "react";
import useForm from "../../../hooks/useForm";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addNewInvoice } from "../../../redux/reducers/invoices/invoiceSlice";
import { FORM_OPTIONS } from "../table.config";
import Select from "../../../shared/Select";
import { fetchClients } from "../../../redux/reducers/clients/clientsSlice";
import { useAuth } from "../../../hooks/useAuth";
import FormLayout from "../../../layouts/FormLayout";
import { selectClientsOptions } from "../../../redux/reducers/clients/clientsSlice";
import JobsTable from "./JobsTable";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import SelectedJobsTable from "./SelectedJobsTable";
import { renderAmount } from "../../../utils";
import { SidebarContainer } from "../../../components";
import Tabs from "../../../shared/Tabs";
import { useGetJobsQuery } from "../../../redux/services/jobs/jobsApi";
import { useFetchAll } from "../../../hooks/useFetchAll";

const InvoiceCreate = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const table1Ref = useRef();
  const table2Ref = useRef();

  const [loading, setLoading] = useState(false);

  const { form, updateForm, clearForm } = useForm(FORM_OPTIONS);

  const clientStatus = useSelector((state) => state.client.status);
  const clientOptions = useSelector(selectClientsOptions);

  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedInvoiceItems, setSelectedInvoiceItems] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (clientStatus === "idle") {
      dispatch(fetchClients(user.parentCompany));
    }
  }, [clientStatus, dispatch, user]);

  // Compute default filters based on form client
  const defaultFilters = useMemo(() => {
    if (!form?.client?._id) return [];

    return [
      { id: "client", value: form.client._id },
      { id: "invoiceId", value: false },
    ];
  }, [form?.client?._id]);

  // Jobs Query with useFetchAll hook for search and pagination functionality
  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch, search },
    queryResult: { data: jobsQueryResult, isLoading: jobsLoading },
  } = useFetchAll({
    queryHook: useGetJobsQuery,
    defaultPageSize: 20,
    defaultFilters,
    options: {
      skip: !form?.client?._id, // Only skip if no client ID
    },
  });

  // Client + un-invoiced scope is applied via defaultFilters → baseFilters in useFetchAll.

  // Extract jobs data from query result - handle different response formats
  const jobs = useMemo(() => {
    if (!jobsQueryResult) return [];

    // Handle different response structures:
    // 1. { jobs: [...], data: { page: ... } } - paginated response
    // 2. { data: { jobs: [...] } } - nested response
    // 3. [...] - direct array
    let jobsArray = [];

    if (Array.isArray(jobsQueryResult)) {
      jobsArray = jobsQueryResult;
    } else if (Array.isArray(jobsQueryResult?.jobs)) {
      jobsArray = jobsQueryResult.jobs;
    } else if (Array.isArray(jobsQueryResult?.data?.jobs)) {
      jobsArray = jobsQueryResult.data.jobs;
    } else if (Array.isArray(jobsQueryResult?.data)) {
      jobsArray = jobsQueryResult.data;
    }

    // Ensure we return an array
    return Array.isArray(jobsArray) ? jobsArray : [];
  }, [jobsQueryResult]);

  // Extract pagination data from query result
  const paginationData = useMemo(() => {
    if (!jobsQueryResult) {
      return {
        pageIndex,
        pageSize,
        pageCount: 0,
        totalPages: 0,
        totalRows: 0,
        hasNext: false,
        hasPrev: false,
      };
    }

    // Handle different response structures
    const data = jobsQueryResult.data || jobsQueryResult;
    const jobsArray = Array.isArray(jobsQueryResult)
      ? jobsQueryResult
      : Array.isArray(jobsQueryResult?.jobs)
      ? jobsQueryResult.jobs
      : Array.isArray(jobsQueryResult?.data?.jobs)
      ? jobsQueryResult.data.jobs
      : Array.isArray(jobsQueryResult?.data)
      ? jobsQueryResult.data
      : [];

    const result = {
      pageIndex,
      pageSize: data?.limit || pageSize || 20,
      pageCount: data?.totalPages || 0,
      totalPages: data?.totalPages || 0,
      totalRows: data?.count || data?.total || jobsArray.length || 0,
      hasNext: data?.hasNext || false,
      hasPrev: data?.hasPrev || false,
    };

    return result;
  }, [pageIndex, pageSize, jobsQueryResult]);

  // Filter out jobs that are already in invoiceItems
  const availableJobs = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];

    // Filter out jobs that are already in invoiceItems to avoid duplicates
    // Handle both ObjectId and string comparisons
    const invoiceJobIds = new Set(
      invoiceItems
        .map((item) => {
          const id = item._id || item;
          return typeof id === "string" ? id : id?.toString();
        })
        .filter(Boolean)
    );

    const filteredJobs = jobs.filter((job) => {
      if (!job || !job._id) return false;
      const jobId = typeof job._id === "string" ? job._id : job._id.toString();
      return !invoiceJobIds.has(jobId);
    });

    return filteredJobs;
  }, [jobs, invoiceItems]);

  function addToInvoice() {
    setInvoiceItems([...invoiceItems, ...selectedJobs]);
    setSelectedJobs([]);
    if (table1Ref.current) {
      table1Ref.current.resetSelectedRows(false);
    }
  }

  function removeFromInvoice() {
    const newInvoiceItems = invoiceItems.filter(
      (x) => !selectedInvoiceItems.includes(x)
    );
    setInvoiceItems(newInvoiceItems);
    setSelectedInvoiceItems([]);
    if (table2Ref.current) {
      table2Ref.current.resetSelectedRows(false);
    }
  }

  // Reset selected jobs when doing a search
  useEffect(() => {
    if (search.useSearch) {
      setSelectedJobs([]);
    }
  }, [search.useSearch]);

  function onFormChange(e) {
    const { value, id } = e;
    updateForm({ value, id });
  }

  async function handleSubmit() {
    setLoading(true);
    const data = {
      jobs: invoiceItems.map((i) => ({ _id: i._id })),
      // total: renderInvoiceTotal(),
      status: "Review",
      ...form,
    };

    const res = await dispatch(
      addNewInvoice({ data, parentId: user.parentCompany })
    ).unwrap();

    if (res.status === "success") {
      setLoading(false);
      navigate(`/invoices/${res.data._id}`);
    } else {
      setLoading(false);
      navigate(`/invoices`);
    }
  }

  const client = form.client && (
    <div className="flex items-center">
      Client:{" "}
      <span className="font-bold text-xl ml-2 flex items-center">
        {form.client.name}{" "}
        <XMarkIcon
          className="h-5 w-5 cursor-pointer"
          onClick={() => {
            clearForm();
            setInvoiceItems([]);
          }}
        />
      </span>
    </div>
  );

  const renderInvoiceTotal = () => {
    let totalInvoiceCharges = invoiceItems
      .map((item) => {
        return item.totalCharges;
      })
      .reduce((partialSum, a) => Number(partialSum) + Number(a), 0);

    return totalInvoiceCharges;
  };

  const totalCharges = (
    <div className="flex items-center">
      Total Charges:{" "}
      <span className="font-bold pl-2 text-xl">
        {renderAmount(renderInvoiceTotal())}
      </span>
    </div>
  );

  const headerRef = useRef();
  const [headerContext, setHeaderContext] = useState();
  useEffect(() => {
    setHeaderContext(headerRef.current);
  }, [headerRef.current]);

  const renderTabHeader = (headerProps) => {
    return <div ref={headerRef}></div>;
  };

  const tabs = [
    {
      id: "Create",
      content: (
        <div className="px-3 pb-3 h-auto">
          <FormLayout
            title={client}
            handleSubmit={handleSubmit}
            actions={totalCharges}
            loading={loading}
          >
            <div className="my-5">
              {!form.client ? (
                <Select
                  id="client"
                  onChange={onFormChange}
                  label="Client"
                  options={clientOptions}
                  placeholder="Select a client"
                />
              ) : (
                <div className="flex flex-col xl:flex-row justify-evenly justify-items-stretch space-x-0 xl:space-x-2">
                  <div className="flex-grow w-full">
                    <JobsTable
                      data={availableJobs}
                      setSelectedJobs={setSelectedJobs}
                      client={form.client}
                      ref={table1Ref}
                      pagination={paginationData}
                      pageIndex={paginationData.pageIndex}
                      pageSize={paginationData.pageSize}
                      onPaginationChange={setPagination}
                      fullTextSearch={handleGlobalSearch}
                      loading={jobsLoading}
                    />
                  </div>
                  <div className="flex-grow-0 self-center z-50">
                    <div className="hidden xl:block">
                      <IconButton
                        icon={ChevronRightIcon}
                        onClick={addToInvoice}
                      />
                      <IconButton
                        icon={ChevronLeftIcon}
                        onClick={removeFromInvoice}
                      />
                    </div>
                    <div className="flex xl:hidden">
                      <IconButton
                        icon={ChevronUpIcon}
                        onClick={removeFromInvoice}
                      />
                      <IconButton
                        icon={ChevronDownIcon}
                        onClick={addToInvoice}
                      />
                    </div>
                  </div>
                  <div className="flex-grow w-full">
                    <SelectedJobsTable
                      setSelectedInvoiceItems={setSelectedInvoiceItems}
                      selectedJobs={invoiceItems}
                      client={form.client}
                      ref={table2Ref}
                    />
                  </div>
                </div>
              )}
            </div>
            <div>{totalCharges}</div>
          </FormLayout>
        </div>
      ),
    },
  ];

  return (
    <SidebarContainer
      // title={title}
      hideSidebar
      header={renderTabHeader}
      // actions={actions}
      // sidebarContent={sidebarContent}
      // sidebarTitle={<h2 className="text-xl bg-white py-2">Communications</h2>}
    >
      {({ parentHeight, parentWidth }) => {
        const offsetTop = headerContext?.offsetTop;
        const offsetHeight = headerContext?.offsetHeight;
        const wrapperStyle = { height: `${parentHeight - offsetTop}px` };
        const panelStyle = {
          height: `${parentHeight - offsetTop - offsetHeight - 38}px`,
        };
        return (
          <div style={wrapperStyle} className="px-3 pb-3 h-auto w-full">
            <Tabs
              className={`w-full overflow-hidden`}
              style={wrapperStyle}
              panelStyle={panelStyle}
              useSearchParams
              panelClasses={`p-2 border-t overflow-y-scroll`}
              tabListClasses="bg-white px-2 h-[38px]"
              tabclassName="mx-1"
              id="job-show"
              // onClick={handleTabClick}
              data={tabs}
            />
          </div>
        );
      }}
    </SidebarContainer>
  );
};

const IconButton = (props) => {
  const Icon = props.icon;
  return (
    <div className="flex flex-col items-center my-4 mx-1">
      <div
        onClick={props.onClick}
        className="border ease-in-out duration-300 hover:border-gray-300 rounded-full p-2 hover:bg-gray-200 cursor-pointer"
      >
        <Icon className="w-6 h-6" />
      </div>
      {props.label && <p className="font-semibold">{props.label}</p>}
    </div>
  );
};

export default InvoiceCreate;
